import React, { useEffect, useRef, useState } from 'react';
import { JcCommonProps, JcSize, JcUtils } from '../../utils/JcUtils';
import { JcRow } from '../JcBox';
import { JcButton } from './JcButton';
import { JcIcon, JcIconType } from '../icon/JcIcon';
import { JcBuiltInIcon } from '../icon/JcBuiltInIcons';

export interface JcCollapsibleOption<T> {
    readonly id: T;
    readonly label: React.ReactNode;
    readonly disabled?: boolean;
}

export interface JcCollapsibleOptionsProps<T> extends JcCommonProps {
    readonly options: JcCollapsibleOption<T>[];
    /** The id of the currently selected option, or undefined while unset. */
    readonly value: T | undefined;
    readonly onChange: (id: T) => void;
    /** Accessible name for the group of options. */
    readonly ariaLabel?: string;
    readonly disabled?: boolean;
    /**
     * Shows every option (none selected) while `value` is undefined. When false
     * (default), an unset control stays collapsed to the `placeholder` pill.
     */
    readonly defaultExpanded?: boolean;
    /** The unset pill's label while collapsed (defaults to "Select"). */
    readonly placeholder?: React.ReactNode;
}

/**
 * A horizontal group of mutually exclusive options that collapses to just the
 * chosen pill once a choice is made. Clicking the collapsed pill reveals every
 * option; picking one (or clicking/tabbing away, or pressing Escape) collapses
 * it back down.
 *
 * Every option is always mounted, so the active choice is one stable,
 * animating node: collapsing slides the other options away while it reflows
 * into place, and expanding grows them back in. The collapsed option reads as
 * a plain status pill, not a selected button — it only takes the selected
 * colour once the choices are revealed.
 */
export const JcCollapsibleOptions = <T,>(props: JcCollapsibleOptionsProps<T>): React.ReactElement => {
    const { options, value, onChange, ariaLabel, disabled, defaultExpanded, placeholder = "Select" } = props;
    const [expanded, setExpanded] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const firstOptionRef = useRef<HTMLButtonElement>(null);

    // Optimistic echo of the just-picked option until the controlled `value`
    // catches up (e.g. after an async write round-trips).
    const [pending, setPending] = useState<T | undefined>(undefined);
    useEffect(() => setPending(undefined), [value]);

    const selected = pending ?? value;
    const unset = selected === undefined;
    const hasPlaceholder = !defaultExpanded;
    const isExpanded = expanded || (!!defaultExpanded && unset);

    // Expanding folds the focused placeholder away; move focus into the options.
    useEffect(() => {
        if (hasPlaceholder && expanded && unset) firstOptionRef.current?.focus();
    }, [hasPlaceholder, expanded, unset]);

    const handleSelect = (id: T) => {
        // The first click on a collapsed control just reveals the options.
        if (!isExpanded) {
            setExpanded(true);
            return;
        }
        setPending(id);
        onChange(id);
        setExpanded(false);
    };

    // Collapse only when focus moves to a real element outside. A null
    // relatedTarget can also mean an inside click on macOS (buttons don't take
    // focus there), and collapsing then would swallow the selection click —
    // outside pointer downs are dismissed by the listener below instead.
    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        if (event.relatedTarget) setExpanded(false);
    };

    // Tapping a <button> doesn't focus it on iOS/Android, so blur alone can't
    // dismiss; watch for pointer downs outside the control as well.
    useEffect(() => {
        if (!expanded) return;
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (target instanceof Node && !rootRef.current?.contains(target)) setExpanded(false);
        };
        document.addEventListener("pointerdown", handlePointerDown);
        return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, [expanded]);

    // Escape dismisses the revealed options and refocuses the collapsed pill.
    useEffect(() => {
        if (!expanded) return;
        const root = rootRef.current;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            setExpanded(false);
            root?.querySelector<HTMLButtonElement>(".jc-collapsible-option-active")?.focus();
        };
        root?.addEventListener("keydown", handleKeyDown);
        return () => root?.removeEventListener("keydown", handleKeyDown);
    }, [expanded]);

    const commonProps = JcUtils.commonProps(props, [
        isExpanded && "jc-collapsible-options-expanded",
        "jc-collapsible-options",
    ]);

    return (
        <JcRow ref={rootRef} {...commonProps} role="group" ariaLabel={ariaLabel} alignCenter onBlur={handleBlur}>
            {options.map((option, index) => {
                const isActive = option.id === selected;
                const optionClassName = JcUtils.classNameString([
                    isActive && "jc-collapsible-option-active",
                    "jc-collapsible-option",
                ]);
                return (
                    <JcButton
                        key={String(option.id)}
                        ref={index === 0 ? firstOptionRef : undefined}
                        className={optionClassName}
                        onClick={() => handleSelect(option.id)}
                        selected={isExpanded && isActive}
                        ariaPressed={isActive}
                        ariaExpanded={isActive ? isExpanded : undefined}
                        disabled={disabled || option.disabled}
                    >
                        {option.label}
                    </JcButton>
                );
            })}
            {/* Last so it folds at the row's trailing edge while the options
                grow beside it, instead of being shoved along as they widen. */}
            {hasPlaceholder && (
                <JcButton
                    className={JcUtils.classNameString([
                        unset && "jc-collapsible-option-active",
                        "jc-collapsible-option-placeholder",
                        "jc-collapsible-option",
                    ])}
                    onClick={() => setExpanded(true)}
                    ariaExpanded={isExpanded}
                    disabled={disabled}
                >
                    {placeholder}
                    <JcIcon
                        className="jc-collapsible-option-caret"
                        type={JcIconType.BUILT_IN}
                        icon={JcBuiltInIcon.CHEVRON_DOWN}
                        size={JcSize.EXTRA_SMALL}
                    />
                </JcButton>
            )}
        </JcRow>
    );
};
